import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import {
  Menu,
  X,
  LogOut,
  LogIn,
  UserPlus,
  ChevronDown,
  ShieldCheck,
  Calendar,
  Home,
  User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Button from "@/components/ui/Button";
import { useUser } from "@/api/hooks/user.hooks";
import { useLogout } from "@/api/hooks/auth.hooks";
import {
  useCustomerProfile,
  useCustomerAppointments,
} from "@/api/hooks/customer.hooks";
import { UserRole } from "@/api/enums";

type MenuLink = {
  to: string;
  label: string;
  icon: LucideIcon;
  showCount?: boolean;
};

const PATIENT_LINKS: MenuLink[] = [
  { to: "/client-area", label: "Client Area", icon: Home },
  {
    to: "/client-area/appointments",
    label: "My Appointments",
    icon: Calendar,
    showCount: true,
  },
  { to: "/client-area/profile", label: "My Profile", icon: User },
];

const ADMIN_LINKS: MenuLink[] = [
  { to: "/admin", label: "Admin Dashboard", icon: Home },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useUser();
  const logout = useLogout();

  const isAdmin = !!user?.roles?.some((r: UserRole) =>
    [
      UserRole.Admin,
      UserRole.SuperAdmin,
      UserRole.PracticeAdministrator,
    ].includes(r),
  );
  const isPatient = !!user?.roles?.includes(UserRole.Patient);

  const { data: profile } = useCustomerProfile({ enabled: isPatient });
  const { data: appointments } = useCustomerAppointments(
    { take: 1, skip: 0 },
    { enabled: isPatient },
  );

  const accountLinks = isPatient ? PATIENT_LINKS : isAdmin ? ADMIN_LINKS : [];
  const displayName = profile
    ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim() ||
      profile.email?.split("@")[0]
    : user?.email?.split("@")[0] || "Account";
  const displayEmail = profile?.email || user?.email || "";
  const count = (appointments as unknown as { total?: number })?.total || 0;

  const closeDetails = (e: React.MouseEvent) => {
    const details = (e.target as HTMLElement).closest("details");
    if (details) details.open = false;
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur shadow-[0_1px_0_0_var(--border-strong)]">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <a href="https://apptlyhealth.com">
            <img
              src="/logo-horizontal.svg"
              alt="Apptly Health"
              className="h-20 w-auto md:h-24 hover:opacity-80 transition-opacity"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-3">
            {user ? (
              accountLinks.length > 0 ? (
                <details className="relative group">
                  <summary className="list-none flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md hover:bg-muted/50 transition-colors">
                    {isAdmin && (
                      <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="text-sm font-medium text-muted-foreground truncate max-w-[120px]">
                      {displayName}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground group-open:rotate-180 transition-transform" />
                  </summary>

                  <div className="absolute right-0 mt-2 w-56 bg-background border border-border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-xs text-muted-foreground">
                        Signed in as
                      </p>
                      <p className="text-sm font-medium text-foreground truncate">
                        {displayEmail || displayName}
                      </p>
                    </div>

                    <nav className="py-1">
                      {accountLinks.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={closeDetails}
                          className="flex items-center justify-between px-4 py-2 text-sm hover:bg-muted/50 transition-colors"
                          activeProps={{
                            className: "bg-primary/10 text-primary font-medium",
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <link.icon className="w-4 h-4" />
                            <span>{link.label}</span>
                          </div>
                          {link.showCount && isPatient && count > 0 && (
                            <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              {count}
                            </span>
                          )}
                        </Link>
                      ))}
                    </nav>

                    <div className="border-t border-border pt-1">
                      <button
                        onClick={(e) => {
                          closeDetails(e);
                          logout.mutate();
                        }}
                        disabled={logout.isPending}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-muted/50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        {logout.isPending ? "Signing out..." : "Sign out"}
                      </button>
                    </div>
                  </div>
                </details>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                >
                  <LogOut className="size-4 mr-2" />
                  {logout.isPending ? "Signing out..." : "Sign out"}
                </Button>
              )
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: "/login",
                      search: { redirect: location.pathname || "/" },
                    })
                  }
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: "/signup",
                      search: { redirect: location.pathname || "/" },
                    })
                  }
                >
                  Create account
                </Button>
              </div>
            )}
          </nav>

          {/* Mobile nav */}
          <details className="md:hidden group">
            <summary className="list-none p-2 rounded-md cursor-pointer">
              <Menu className="size-6 group-open:hidden" />
              <X className="size-6 hidden group-open:block" />
            </summary>

            <div className="absolute left-0 right-0 top-full border-b border-border bg-background shadow-lg">
              <nav className="container mx-auto px-4 py-3 flex flex-col gap-2">
                {user ? (
                  <>
                    {accountLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={closeDetails}
                        className="flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors"
                        activeProps={{
                          className: "bg-primary/10 text-primary font-medium",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <link.icon className="w-4 h-4" />
                          <span>{link.label}</span>
                        </div>
                        {link.showCount && isPatient && count > 0 && (
                          <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            {count}
                          </span>
                        )}
                      </Link>
                    ))}
                    <div className="border-t border-border my-2" />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        closeDetails(e);
                        logout.mutate();
                      }}
                      disabled={logout.isPending}
                    >
                      <LogOut className="size-4 mr-2" />
                      {logout.isPending ? "Signing out..." : "Sign out"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() =>
                        navigate({
                          to: "/login",
                          search: { redirect: location.pathname || "/" },
                        })
                      }
                    >
                      <LogIn className="size-4 mr-2" />
                      Sign In
                    </Button>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() =>
                        navigate({
                          to: "/signup",
                          search: { redirect: location.pathname || "/" },
                        })
                      }
                    >
                      <UserPlus className="size-4 mr-2" />
                      Create account
                    </Button>
                  </>
                )}
              </nav>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
